import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Check, ArrowLeft } from 'lucide-react';
import { initiatePayment } from '@/lib/api';
import { SiteFooter } from '@/components/SiteFooter';

const PLANS = [
  {
    plan: 'basic', name: 'Report', price: '499', tagline: 'Understand your numbers',
    features: [
      'Complete financial breakdown with GST',
      'Revenue, costs & profit analysis',
      'All risk flags with explanations',
      'AI-powered fix suggestions',
      'Data confidence & execution assessment',
      'PDF report download',
    ],
  },
  {
    plan: 'scenario_pack', name: 'Report + Scenarios', price: '999', popular: true, tagline: 'Test before you commit',
    features: [
      'Everything in Report plan',
      '3 what-if scenario tests',
      'Change rent, AOV, staff, food cost',
      'Side-by-side scenario comparison',
      'Find your best-case numbers',
    ],
  },
  {
    plan: 'pro', name: 'Full Analysis', price: '2,999', tagline: 'Investor-ready clarity',
    features: [
      'Everything in Scenarios plan',
      '5 scenario credits',
      'Detailed cost optimization insights',
      'Benchmark comparison with similar concepts',
      'Priority consultation access',
    ],
  },
];

export default function Plans() {
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const handleBuy = (plan) => {
    if (!user) { login(); return; }
    initiatePayment(plan, null, user, () => {
      navigate('/assess');
    });
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button onClick={() => navigate('/')}><img src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/z3ko7m68_Cake_Studio.png" alt="ROCH" className="h-14 w-auto" /></button>
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-xs text-[#1D1D1F]/60 rounded-full">Dashboard</Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="text-center mb-10">
          <p className="text-xs tracking-widest uppercase font-bold text-[#1D1D1F]/40 mb-3">Pricing</p>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1D1D1F] tracking-tight mb-3">
            Choose the right plan for you.
          </h1>
          <p className="text-sm text-[#1D1D1F]/50 font-medium max-w-md mx-auto">
            One-time payment per report. No subscriptions. Get the clarity you need before investing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {PLANS.map((tier) => (
            <div
              key={tier.plan}
              data-testid={`plan-card-${tier.plan}`}
              className={`bg-white rounded-3xl border p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)] ${
                tier.popular ? 'border-[#1D1D1F] shadow-[0_8px_32px_rgba(0,0,0,0.04)]' : 'border-[#E5E5EA]'
              }`}
            >
              {tier.popular && (
                <span className="text-[9px] font-bold text-white bg-[#1D1D1F] rounded-full px-3 py-1 mb-4 inline-block">MOST POPULAR</span>
              )}
              <h3 className="text-lg font-bold text-[#1D1D1F]">{tier.name}</h3>
              <p className="text-xs text-[#1D1D1F]/40 font-medium mb-3">{tier.tagline}</p>
              <div className="text-3xl font-bold text-[#1D1D1F] mb-6">₹{tier.price}</div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[#1D1D1F]/70 font-medium">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`buy-${tier.plan}-btn`}
                onClick={() => handleBuy(tier.plan)}
                className={`w-full rounded-full py-3 text-sm font-semibold transition-all duration-500 active:scale-95 ${
                  tier.popular
                    ? 'bg-[#1D1D1F] text-white hover:bg-[#434344]'
                    : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]'
                }`}
              >
                Get {tier.name}
              </Button>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-xs text-[#1D1D1F]/30 font-medium mb-4">All plans include the full feasibility score, model detection, and risk assessment.</p>
          <button onClick={() => navigate(-1)} className="text-xs text-[#0071E3] font-semibold hover:underline flex items-center gap-1 mx-auto">
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> Go back
          </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}